import { ApiClient } from '../utils/api';
import { PurchaseItem, PurchaseCatalog, PurchaseURLOptions, LoginURLOptions, Transaction, TipURLOptions } from '../types';
import { generatePurchaseURL as generateURL } from '../utils/url';
import { generateLoginURL, generateTipURL } from '../utils/url';

/**
 * Service for handling purchases and transactions
 * 
 * IMPORTANT: Before using methods that require authentication (like getPurchasedItems),
 * ensure that a valid session has been initialized through AuthService.initializeGameServer().
 * Without a valid session token, these methods will return empty results.
 */
export class PurchaseService {
  private apiClient: ApiClient;
  private gameId: string;
  private storeBaseUrl: string;

  constructor(apiClient: ApiClient, gameId: string, storeBaseUrl: string) {
    this.apiClient = apiClient;
    this.gameId = gameId;
    this.storeBaseUrl = storeBaseUrl;
  }

  /**
   * Get the store catalog for a game
   * @returns Promise resolving to an array of purchasable items
   */
  async getCatalog(): Promise<PurchaseItem[]> {
    try {
      console.log(`Fetching catalog for game: ${this.gameId}`);
      const url = `/api/store/catalog?gameId=${this.gameId}`;
      
      const response = await this.apiClient.get<{ items: PurchaseItem[] }>(url);

      if (response.items) {
        console.log(`Retrieved ${response.items?.length || 0} items from catalog`);
        return response.items || [];
      }

      if (!response) {
        console.error('No data in catalog response');
      } else if (!response.items) {
        console.error('No items in catalog response data');
      }

      return [];
    } catch (error) {
      console.error('Failed to get store catalog:', error);
      return [];
    }
  }

  /**
   * Get a specific item by ID
   * @param itemId Item ID
   * @returns Promise resolving to the item or null if not found
   */
  async getItem(itemId: string): Promise<PurchaseItem | null> {
    try {
      let url = `/api/store/items/${itemId}`;
      
      const response = await this.apiClient.get<{ item: PurchaseItem }>(url);

      if (response && response.item) {
        return response.item;
      }

      return null;
    } catch (error) {
      console.error('Failed to get item:', error);
      return null;
    }
  }

  /**
   * Generate a purchase URL without initiating a purchase
   * This URL can be used to redirect the user to the web store to complete the purchase
   * 
   * @param options Purchase URL options including itemId and sessionToken
   * @returns The generated purchase URL
   */
  generatePurchaseURL(options: PurchaseURLOptions): string {
    // Ensure gameId is included in the URL
    const baseUrl = `${this.storeBaseUrl}?gameId=${encodeURIComponent(this.gameId)}`;
    
    // Validate that a session token is provided
    if (!options.sessionToken) {
      throw new Error('Session token is required to generate a purchase URL');
    }
    
    // Generate the URL with the correct format: /store/items/{itemId}?gameId={gameId}&affiliateCode={affiliateCode}
    return generateURL(
      baseUrl,
      options.itemId,
      options.sessionToken,
      options.affiliateCode,
      options.redirectUrl
    );
  }

  /**
   * Generate a login URL for user authentication and session association
   * 
   * @param options Login URL options including sessionToken and gameId
   * @returns The generated login URL
   */
  generateLoginURL(options: LoginURLOptions): string {
    // Generate the URL with the correct format: /auth/login?sessionToken={sessionToken}&gameId={gameId}

    if (!options.sessionToken) {
      throw new Error('Session token is required to generate a login URL');
    }

    if(!options.gameId) {
      throw new Error('Game ID is required to generate a login URL');
    }

    return generateLoginURL(
      this.storeBaseUrl,
      options.sessionToken,
      options.gameId,
      options.redirectUrl
    );
  }

  /**
   * Generate a tip URL for users to give a tip to the developer
   * 
   * @param options Tip URL options including sessionToken and gameId
   * @returns The generated tip URL
   */
  generateTipURL(options: TipURLOptions): string {
    // Generate the URL with the correct format: /store/tip?sessionToken={sessionToken}&gameId={gameId}

    if (!options.sessionToken) {
      throw new Error('Session token is required to generate a tip URL');
    }

    if(!options.gameId) {
      throw new Error('Game ID is required to generate a tip URL');
    }

    return generateTipURL(
      this.storeBaseUrl,
      options.sessionToken,
      options.gameId,
      options.amount,
      options.redirectUrl
    );
  }

  /**
   * Get purchased items for the current user
   * @returns Promise resolving to an array of purchased items
   */
  async getPurchasedItems(): Promise<PurchaseItem[]> {
    try {
      // Get the session token from the API client
      const sessionToken = this.apiClient.getSessionToken();
      
      if (!sessionToken) {
        console.warn('No session token available. Please initialize a session first.');
        return [];
      }
      
      const response = await this.apiClient.get<{ transactions: Transaction[] }>(
        `/api/store/purchases?gameId=${this.gameId}&sessionToken=${sessionToken}`
      );

      if (response && response.transactions) {
        // For each transaction, get the item details
        const items: PurchaseItem[] = [];
        
        for (const transaction of response.transactions) {
          if (transaction.status === 'completed' && transaction.metadata && transaction.metadata.itemId) {
            const itemResponse = await this.getItem(transaction.metadata.itemId);
            if (itemResponse) {
              items.push(itemResponse); 
            }
          }
        }
        
        return items;
      }

      return [];
    } catch (error) {
      console.error('Failed to get purchased items:', error);
      return [];
    }
  }

  /**
   * Get transaction history for the current user
   * @returns Promise resolving to an array of transactions
   */
  async getTransactionHistory(): Promise<Transaction[]> {
    try {
      const sessionToken = this.apiClient.getSessionToken();
      
      if (!sessionToken) {
        console.warn('No session token available. Please initialize a session first.');
        return [];
      }

      const response = await this.apiClient.get<{ transactions: Transaction[] }>(
        `/api/store/purchases?gameId=${this.gameId}&sessionToken=${sessionToken}`
      );

      if (response && response.transactions) {
        return response.transactions;
      }

      return [];
    } catch (error) {
      console.error('Failed to get transaction history:', error);
      return [];
    }
  }

  /**
   * Sync transaction history from the server for the current user
   * @returns Promise resolving to a boolean indicating success
   */
  async syncTransactionHistory(): Promise<boolean> {
    try {
      // This is now handled by getTransactionHistory
      const transactions = await this.getTransactionHistory();
      return transactions.length > 0;
    } catch (error) {
      console.error('Failed to sync transaction history:', error);
      return false;
    }
  }
} 